import { List } from "@refinedev/antd";
import { useApiUrl, useNotification } from "@refinedev/core";
import { Alert, Button, Form, InputNumber, Modal, Space, Table } from "antd";
import axios from "axios";
import prettyBytes from "pretty-bytes";
import React, { useCallback, useEffect, useState } from "react";
import { MAX_EMAIL_SIZE_CEILING_BYTES } from "../../constants/emailSize";

interface IVesselEmailSetting {
  id: number;
  name: string;
  imoNumber: string;
  maxEmailSizeKb: number | null;
}

interface IVesselEmailSettingsResponse {
  data: IVesselEmailSetting[];
  floorBytes: number;
  chunkLimit: number;
  typicalUpdateBytes: number;
  largeUpdateBytes: number;
}

// The stored cap is the size of the email on the wire. The backend divides by
// this to get the 7z volume, so the estimate here has to divide by it too or
// the page would promise fewer emails than the vessel receives.
const EMAIL_WIRE_OVERHEAD = 1.4;

// The GET response has no ceiling field (VesselEmailSettingsList only carries
// floorBytes), so this cannot be sourced the way the floor is. It mirrors the
// server's hard cap instead. Shared with the Global Settings floor input so
// the two portal pages cannot disagree about the same bound.
const CEILING_BYTES = MAX_EMAIL_SIZE_CEILING_BYTES;

function estimateEmails(payloadBytes: number, capBytes: number): number {
  const volume = Math.floor(capBytes / EMAIL_WIRE_OVERHEAD);
  if (volume <= 0) return 0;
  return Math.ceil(payloadBytes / volume);
}

export const VesselEmailSettingsPage: React.FC = () => {
  const apiURL = useApiUrl();
  const { open } = useNotification();

  const [response, setResponse] = useState<IVesselEmailSettingsResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<IVesselEmailSetting | null>(null);
  const [form] = Form.useForm();

  const authHeaders = useCallback(
    () => ({
      withCredentials: false,
      headers: { Authorization: `Bearer ${localStorage.getItem("emr-auth")}` },
    }),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get<IVesselEmailSettingsResponse>(
        `${apiURL}/vessel-email-settings`,
        authHeaders()
      )
      .then(({ data }) => setResponse(data))
      .catch(() =>
        open?.({
          type: "error",
          message: "Could not load email settings",
          description: "Error",
        })
      )
      .finally(() => setLoading(false));
  }, [apiURL, authHeaders, open]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      // antd already renders the field-level message; nothing else to do.
      return;
    }

    try {
      await axios.patch(
        `${apiURL}/vessel-email-settings/${editing?.id}`,
        { maxEmailSizeBytes: Math.round(values.maxEmailSizeMb * 1000000) },
        authHeaders()
      );
      open?.({
        type: "success",
        message: "Max email size updated",
        description: "Success",
      });
      setEditing(null);
      load();
    } catch (err: any) {
      // Below-floor, above-ceiling and "vessel not found" each arrive as a
      // distinct server message; surface it instead of a generic string.
      const apiMessage = err?.response?.data?.message;
      open?.({
        type: "error",
        message: "Could not update max email size",
        description: apiMessage || err?.message || "An error occurred",
      });
    }
  };

  // Only real, undefined until the GET resolves -- never guessed, so the
  // page cannot print a number the server does not actually have configured.
  const floorBytes = response?.floorBytes;
  const chunkLimit = response?.chunkLimit;
  // Numeric MB thresholds used for form constraints only (never printed
  // directly). 0 is a harmless placeholder here: the modal that reads it can
  // only open from a table row, which only exists once response has loaded.
  const floorMb = (floorBytes ?? 0) / 1000000;
  const ceilingMb = CEILING_BYTES / 1000000;
  const typical = response?.typicalUpdateBytes ?? 0;

  return (
    <List title="Email Settings" canCreate={false}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Set the largest single email each vessel's mailbox accepts"
        description={
          floorBytes != null && chunkLimit != null ? (
            <span>
              Each update to the vessel is split into emails no larger than
              the size you set here, so a higher value means fewer, larger
              emails. The minimum is {prettyBytes(floorBytes)}; the maximum is{" "}
              {prettyBytes(CEILING_BYTES)}. An update needing more than{" "}
              {chunkLimit} emails is not sent, and the vessel is notified
              instead.
            </span>
          ) : (
            <span>Loading configured limits…</span>
          )
        }
      />

      <Table
        dataSource={response?.data ?? []}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 25 }}
      >
        <Table.Column dataIndex="name" title="Vessel" />
        <Table.Column dataIndex="imoNumber" title="IMO" />
        <Table.Column
          dataIndex="maxEmailSizeKb"
          title="Max Email Size"
          render={(value: number | null) =>
            value ? prettyBytes(Number(value)) : "-"
          }
        />
        <Table.Column
          title="Emails per typical update"
          render={(_, record: IVesselEmailSetting) =>
            record.maxEmailSizeKb
              ? estimateEmails(typical, Number(record.maxEmailSizeKb))
              : "-"
          }
        />
        <Table.Column
          title="Actions"
          render={(_, record: IVesselEmailSetting) => (
            <Space>
              <Button
                size="small"
                onClick={() => {
                  setEditing(record);
                  form.setFieldsValue({
                    maxEmailSizeMb: record.maxEmailSizeKb
                      ? Number(record.maxEmailSizeKb) / 1000000
                      : floorMb,
                  });
                }}
              >
                Edit
              </Button>
            </Space>
          )}
        />
      </Table>

      <Modal
        open={Boolean(editing)}
        title={`Max Email Size: ${editing?.name ?? ""}`}
        onCancel={() => setEditing(null)}
        onOk={onSave}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Max Email Size (MB)"
            name="maxEmailSizeMb"
            extra={`Values above ${ceilingMb} MB are rejected: past that point the update would go out as a single oversized email instead of being split, which is the failure this page exists to prevent.`}
            rules={[
              { required: true, message: "Enter a size" },
              {
                type: "number",
                min: floorMb,
                message: `Minimum is ${prettyBytes(floorBytes ?? 0)}`,
              },
              {
                type: "number",
                max: ceilingMb,
                message: `Maximum is ${prettyBytes(CEILING_BYTES)}`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={floorMb}
              max={ceilingMb}
              step={0.5}
            />
          </Form.Item>
        </Form>

        <p>Emails per update at each size:</p>
        <Table
          size="small"
          pagination={false}
          rowKey="cap"
          dataSource={Array.from(new Set([floorMb, 6, 8, 10])).map((mb) => ({
            cap: mb,
            typical: estimateEmails(
              response?.typicalUpdateBytes ?? 0,
              mb * 1000000
            ),
            large: estimateEmails(
              response?.largeUpdateBytes ?? 0,
              mb * 1000000
            ),
          }))}
        >
          <Table.Column
            dataIndex="cap"
            title="MB"
            render={(mb: number) => Number(mb.toFixed(2))}
          />
          <Table.Column dataIndex="typical" title="Typical update" />
          <Table.Column dataIndex="large" title="Large update" />
        </Table>
      </Modal>
    </List>
  );
};
